import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useDebounce } from '@uidotdev/usehooks'
import React from 'react'
import { useEvent } from 'react-use'

import GlobalSearchDialog from '@/components/misc/GlobalSearch/components/GlobalSearchDialog'
import SearchButton from '@/components/misc/GlobalSearch/components/SearchButton'
import SearchInput from '@/components/misc/GlobalSearch/components/SearchInput'
import { useGlobalSearch } from '@/components/misc/GlobalSearch/hooks/useGlobalSearch'
import { useIsMinWidthLg } from '@/hooks/useIsMinWidthLg'
import { useIsMinWidthXl } from '@/hooks/useIsMinWidthXl'
import { isMacOS } from '@/lib/helpers/helperFunctions'
import { QUERY_KEYS } from '@/lib/queryKeys'
import companyService from '@/lib/services/companyService'

const GlobalSearch: React.FC = () => {
  const globalSearch = useGlobalSearch()
  const debouncedSearchTerm = useDebounce(globalSearch.searchTerm, 400)
  const isMinWidthLg = useIsMinWidthLg()
  const isMinWidthXl = useIsMinWidthXl()
  const width = isMinWidthXl ? '450px' : undefined

  const globalSearchQuery = useQuery({
    queryKey: QUERY_KEYS.companyGlobalSearch(debouncedSearchTerm),
    queryFn: () => companyService.globalSearch(debouncedSearchTerm),
    enabled: !!debouncedSearchTerm,
    refetchOnWindowFocus: false,
    staleTime: 20000,
    placeholderData: keepPreviousData,
  })

  useOpenShortcut()

  return (
    <div
      className="flex shrink-0 grow"
      style={{ width: width, maxWidth: width }}
    >
      {isMinWidthLg ? <SearchInput /> : <SearchButton />}

      <GlobalSearchDialog
        globalSearchQuery={globalSearchQuery}
        // While the debounce hasn't caught up, the query still holds the
        // previous term's (possibly cached) results — treat that as loading so
        // stale results don't flash.
        isDebouncing={globalSearch.searchTerm !== debouncedSearchTerm}
      />
    </div>
  )
}

/** Opens the search palette with Cmd+K (macOS) or Ctrl+/ (Windows & Linux). */
function useOpenShortcut() {
  const globalSearch = useGlobalSearch()

  useEvent('keydown', function (event: KeyboardEvent) {
    const shouldOpen = isMacOS()
      ? event.metaKey && event.key === 'k'
      : event.ctrlKey && event.key === '/'

    if (shouldOpen) {
      event.preventDefault() // stop Firefox from focusing the location bar
      globalSearch.show()
    }
  })
}

export default GlobalSearch
